import { SxProps } from '@mui/system/styleFunctionSx';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import TextField from '@mui/material/TextField';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import Button from '@mui/material/Button';
import { GradientTypography } from '../gradients/GradientTypography';
import { SocialIcon, SocialIconProps } from './SocialIcon';
import { Link } from '../Link';
import { DialogCentered } from '../DialogCentered';
import { useState } from 'react';
import { Social, SocialName } from 'functions/src/types/Social';
import { LoadingButton } from '../buttons/LoadingButton';

export type SocialIconsProps = {
  linkedSocials?: Social[];
  wrapperSX?: SxProps;
  gradient?: boolean;
  onRemoveSocial?: (social: Social) => Promise<void>;
  onAddSocial?: (social: Social) => Promise<void>;
} & Omit<SocialIconProps, 'social'>;

// The order in which the linkedSocials array will be sorted to ensure the icons appear in the same order for every game
const SOCIALS_ORDER: SocialName[] = [
  'twitter',
  'discord',
  'telegram',
  'twitch',
  'youtube',
  'medium',
  'tiktok',
  'instagram',
  'facebook',
  'github',
  'linkedin',
  'reddit',
  'website',
  'blumint',
];

const SOCIAL_URL_TO_NAME: { [urlPrefix: string]: SocialName } = {
  'twitter.com/': 'twitter',
  't.co/': 'twitter',
  'discordapp.com/': 'discord',
  't.me/': 'telegram',
  'telegram.me/': 'telegram',
  'twitch.tv/': 'twitch',
  'youtube.com/': 'youtube',
  'youtu.be/': 'youtube',
  'm.youtube.com/': 'youtube',
  'medium.com/': 'medium',
  'tiktok.com/': 'tiktok',
  'm.tiktok.com/': 'tiktok',
  'instagram.com/': 'instagram',
  'm.instagram.com/': 'instagram',
  'facebook.com/': 'facebook',
  'fb.me/': 'facebook',
  'github.com/': 'github',
  'linkedin.com/': 'linkedin',
  'reddit.com/': 'reddit',
};

export function SocialIcons({
  linkedSocials = [],
  wrapperSX = {},
  onRemoveSocial,
  onAddSocial,
  ...socialIconProps
}: SocialIconsProps) {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [social, setSocial] = useState<Social | null>(null);

  const handleNewSocialChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!!event.target.value) {
      const value = event.target.value
        .replace('https://', '')
        .replace('www.', '')
        .trim()
        .toLowerCase();
      const urlPrefix = Object.keys(SOCIAL_URL_TO_NAME).find((url) =>
        value.startsWith(url),
      );
      if (urlPrefix) {
        const socialObj: Social = {
          name: SOCIAL_URL_TO_NAME[`${urlPrefix}`],
          link: value,
        };
        setSocial(socialObj);
        return;
      }
    }
    setSocial(null);
  };

  const orderedSocials: Social[] = linkedSocials.sort(
    (a, b) => SOCIALS_ORDER.indexOf(a.name) - SOCIALS_ORDER.indexOf(b.name),
  );

  const socialsIcons: (JSX.Element | undefined)[] = orderedSocials.map(
    (social) => {
      if (!social.name || !social.link) return undefined;

      const link = `https://${social.link.replace('https://', '')}`;

      if (!onRemoveSocial) {
        return (
          <Link sx={{ mx: '-4px' }} href={link} key={social.name}>
            <SocialIcon social={social} {...socialIconProps} />
          </Link>
        );
      }
      return (
        <Badge
          key={social.name}
          overlap="circular"
          badgeContent={
            <CancelRoundedIcon
              color="error"
              sx={{
                width: '20px',
                '&:hover': {
                  cursor: 'pointer',
                },
              }}
              onClick={() => onRemoveSocial(social)}
            />
          }
          sx={{
            mx: '-4px',
          }}
        >
          <SocialIcon social={social} {...socialIconProps} />
        </Badge>
      );
    },
  );

  return (
    <>
      <Box
        display={'flex'}
        flexDirection={'row'}
        flexWrap={'wrap'}
        justifyContent={'center'}
        rowGap={2}
        alignItems={'center'}
        sx={{
          '> a': {
            mx: '-4px',
          },
          ...wrapperSX,
        }}
      >
        {socialsIcons.map((icon) => icon)}
        {onAddSocial && (
          <Button
            startIcon={<AddRoundedIcon />}
            variant="contained"
            onClick={() => setIsAddDialogOpen(true)}
            sx={{ ml: 4 }}
          >
            Link Social
          </Button>
        )}
      </Box>
      <DialogCentered
        open={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        innerSx={{
          columnGap: 2,
          flexDirection: 'column',
        }}
        showCloseIcon={true}
      >
        <GradientTypography
          variant="h5"
          textTransform="uppercase"
          gradientColor="primary.vertical"
          sx={{ mb: 8 }}
        >
          Link your social media
        </GradientTypography>
        <Stack direction="row" gap={2} sx={{ alignItems: 'center' }}>
          <TextField
            label="Social Media Link"
            variant="outlined"
            size="small"
            color="primary"
            placeholder="https://twitter.com/jack"
            onChange={handleNewSocialChange}
            focused
            sx={{
              width: '300px',
            }}
          />
          <LoadingButton
            variant="contained"
            disabled={!social}
            size="large"
            sx={{ height: '40px' }}
            onClick={
              !!social
                ? async () => {
                    if (!!onAddSocial) {
                      await onAddSocial(social);
                    }
                    setIsAddDialogOpen(false);
                  }
                : undefined
            }
          >
            Submit
          </LoadingButton>
        </Stack>
      </DialogCentered>
    </>
  );
}
